import { MutableRefObject, RefObject, useEffect, useRef, useState } from 'react';
import invariant from 'tiny-invariant';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import { attachClosestEdge, extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import type { TaskStatus } from '../../types';

type Edge = 'top' | 'bottom';

export type TaskDragData = {
  taskId: number;
  currentStatus: TaskStatus;
  index: number;
};

type TaskDraggable = {
  elementRef: RefObject<any>;
  handleRef: MutableRefObject<any>;
  closestEdge: Edge | null;
  isDragging: boolean;
};

export function useTaskDraggable(
  taskId: number,
  currentStatus: TaskStatus,
  index: number,
  isDisabled: boolean,
): TaskDraggable {
  const elementRef = useRef<HTMLElement>(null);
  const handleRef = useRef<Element | undefined>(undefined);

  const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  useEffect(() => {
    invariant(elementRef.current);
    const element = elementRef.current;
    const dragData: TaskDragData = { taskId, currentStatus, index };

    return combine(
      draggable({
        element,
        dragHandle: handleRef.current,
        canDrag: () => !isDisabled,
        getInitialData: () => dragData,
        onGenerateDragPreview({ nativeSetDragImage }) {
          disableNativeDragPreview({ nativeSetDragImage });
        },
        onDragStart() {
          setIsDragging(true);
        },
        onDrop() {
          setIsDragging(false);
        },
      }),
      dropTargetForElements({
        element,
        canDrop({ source }) {
          const sourceData = source.data as TaskDragData;
          return sourceData.taskId !== taskId;
        },
        getData({ input }) {
          return attachClosestEdge(dragData, {
            element,
            input,
            allowedEdges: index === 0 ? ['top', 'bottom'] : ['bottom'],
          });
        },
        onDrag({ self, source }) {
          const isSource = source.element === element;
          if (isSource) {
            setClosestEdge(null);
            return;
          }

          const closestEdge = extractClosestEdge(self.data) as Edge;
          const sourceData = source.data as TaskDragData;

          if (sourceData.currentStatus === currentStatus) {
            const isItemBeforeSource = index === sourceData.index - 1;
            const isItemAfterSource = index === sourceData.index + 1;

            const isDropIndicatorHidden =
              (isItemBeforeSource && closestEdge === 'bottom') || (isItemAfterSource && closestEdge === 'top');

            if (isDropIndicatorHidden) {
              setClosestEdge(null);
              return;
            }
          }

          setClosestEdge(closestEdge);
        },
        onDragLeave() {
          setClosestEdge(null);
        },
        onDrop() {
          setClosestEdge(null);
        },
      }),
    );
  }, [taskId, currentStatus, index, isDisabled]);

  return {
    elementRef,
    handleRef,
    closestEdge,
    isDragging,
  };
}
